import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CourseCard } from '../components/Courses/CourseCard';
import { CourseFilters } from '../components/Courses/Filters/CourseFilters';
import { CourseForm } from '../components/Courses/CourseForm';
import { SkeletonGrid } from '../components/ui/SkeletonCard';
import { Hero } from '../components/Hero';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';
import type { Course } from '../types/courses';

interface Filters {
  categories: string[];
  languages: string[];
  priceRange: string;
  contentType: string;
}

export function Courses() {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filters, setFilters] = useState<Filters>({
    categories: [],
    languages: [],
    priceRange: '',
    contentType: ''
  });

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('is_admin')
          .eq('id', user.id)
          .single();
        setIsAdmin(!!data?.is_admin);
      }
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    async function fetchCourses() {
      try {
        let query = supabase
          .from('courses')
          .select(`
            *,
            user:users (
              username,
              avatar_url,
              user_type,
              subscription_status,
              manual_professional
            )
          `)
          .eq('approval_status', 'approved')
          .eq('user.user_type', 'professional');

        // Apply category filter
        if (filters.categories.length > 0) {
          query = query.in('category', filters.categories);
        }

        // Apply language filter
        if (filters.languages.length > 0) {
          query = query.in('language', filters.languages);
        }

        // Apply price range filter
        if (filters.priceRange) {
          switch (filters.priceRange) {
            case 'free':
              query = query.eq('price', 0);
              break;
            case 'under_50':
              query = query.gt('price', 0).lte('price', 50);
              break;
            case '50_100':
              query = query.gt('price', 50).lte('price', 100);
              break;
            case 'over_100':
              query = query.gt('price', 100);
              break;
          }
        }

        const { data, error: fetchError } = await query;
        if (fetchError) throw fetchError;

        // Batch-fetch all lessons for the returned courses in a single query (fixes N+1)
        const courseIds = (data || []).map(c => c.id);
        let lessonsByType: Record<string, { count: number; types: string[] }> = {};

        if (courseIds.length > 0) {
          const { data: allLessons } = await supabase
            .from('lessons')
            .select('course_id, type')
            .in('course_id', courseIds);

          // Group lessons by course_id
          for (const lesson of allLessons || []) {
            if (!lessonsByType[lesson.course_id]) {
              lessonsByType[lesson.course_id] = { count: 0, types: [] };
            }
            lessonsByType[lesson.course_id].count++;
            lessonsByType[lesson.course_id].types.push(lesson.type);
          }
        }

        const coursesWithDetails = (data || []).map(course => {
          const lessonInfo = lessonsByType[course.id] || { count: 0, types: [] };
          const isQuiz = lessonInfo.count > 0 && lessonInfo.types.every(t => t === 'quiz');
          return {
            ...course,
            _count: { lessons: lessonInfo.count },
            isQuiz
          };
        });

        // Apply content type filter (client-side since it depends on lesson types)
        let filteredCourses = coursesWithDetails;
        if (filters.contentType === 'quiz') {
          filteredCourses = coursesWithDetails.filter(c => c.isQuiz);
        } else if (filters.contentType === 'course') {
          filteredCourses = coursesWithDetails.filter(c => !c.isQuiz);
        }

        setCourses(filteredCourses);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchCourses();
  }, [filters, refreshKey]);

  const handleFilterChange = (filterType: keyof Filters, values: string[]) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: (filterType === 'priceRange' || filterType === 'contentType') ? values[0] || '' : values
    }));
  };

  return (
    <div>
      <Meta 
        title={`${t('courses.title')} | Mindful Family`}
        description={t('courses.subtitle')}
      />
      
      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/blog-images/59bed50f-5ccf-4265-87fa-7743af34d361/Man%20teaching%20a%20woman%20about%20wellness%20courses.webp"
          alt="Explore, Learn, and Grow at Your Own Pace"
          className="w-full h-full object-cover shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 py-8 text-center bg-gray-50">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('courses.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('courses.subtitle')}
        </p>
        {isAdmin && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-accent-text text-white rounded-lg font-medium hover:bg-accent-text/90 transition-colors shadow-sm"
          >
            {t('courses.submitListing')}
          </button>
        )}
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block">
        <Hero
          title={t('courses.title')}
          subtitle={t('courses.subtitle')}
          image="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/blog-images/59bed50f-5ccf-4265-87fa-7743af34d361/Man%20teaching%20a%20woman%20about%20wellness%20courses.webp"
          showAddListing={isAdmin}
          onAddListing={isAdmin ? () => setShowCreateModal(true) : undefined}
          addListingButtonText={t('courses.submitListing')}
        />
      </div>

      <div className="px-4 sm:px-6 lg:px-10 mt-8">
        <div className="max-w-page mx-auto py-12">
        <CourseFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {error && (
          <div className="text-red-600 mb-4">{error}</div>
        )}

        {loading ? (
          <SkeletonGrid count={6} />
        ) : courses.length === 0 ? (
          <div className="bg-background rounded-lg p-8 text-center">
            <p className="text-content">{t('courses.noCoursesFilter')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
        </div>
      </div>
      
      {showCreateModal && (
        <CourseForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}